/* eslint-disable camelcase */
import { range, sumBy } from 'lodash';
import {
    MAX_HOURS,
    LIKERT_SCALE_TYPES,
    QUESTION_TYPES,
    REACTION_TYPES,
    VISIBILITY_MIN_ANSWERS,
    MIN_ANSWER_LEVEL,
    TestQuizTypes,
    DistributionCategoryFormats,
    DistributionCategoryCardFormats,
    VISIBILITY_SHUFFLE_ANSWERS,
    QuizCardFormats,
    QuizFileTypes,
} from '@/components/Blocks/BlockTestOrQuiz/constants.js';
import { SCORE_TYPE } from '@/constants/index.js';

/**
 * @param {string} questioType
 * @returns {{
 *      likertType: (string|null),
 *      questionContent: (null|string),
 *      reactionType: string,
 *      minAnswersLevel: (null|number),
 *      isShaked: boolean,
 *      generalAnswerReaction: (null|object),
 *      withOther: (null|boolean),
 *      distribFormatCategory: string,
 *      distribFormatCard: string,
 * }}
 */
export const getDefaultQuestionOptions = ({ quizType, questionType }) => ({
    isShaked: quizType === TestQuizTypes.TEST && VISIBILITY_SHUFFLE_ANSWERS.includes(questionType),
    isAnswerOneByOne: false,
    minAnswersLevel: null,
    likertType: questionType === QUESTION_TYPES.LIKERT_SCALE ? LIKERT_SCALE_TYPES.NUMBER : null,
    withOther: null, // boolean|null
    generalAnswerReaction: null, // null | { correct: string, error: string }
    reactionType: REACTION_TYPES.NONE,
    questionContent: null,
    answerComment: false,
    distribFormatCategory: DistributionCategoryFormats.TEXT,
    distribFormatCard: DistributionCategoryCardFormats.TEXT,
    quizCardFormat: QuizCardFormats.TEXT,
    fileTypes: Object.values(QuizFileTypes),
    scoreCount: quizType === TestQuizTypes.TEST && questionType !== QUESTION_TYPES.INFORMATION ? 1 : null,
});

export const checkDuration = val => {
    if (val) {
        const result = /(\d+?):(\d+?)/.exec(val);

        if (result) {
            const [, hours, minutes] = result;

            if (Number(hours) > MAX_HOURS || (Number(hours) >= MAX_HOURS && Number(minutes) > 0)) {
                return false;
            }
        }
    }

    return true;
};

export const getCountItems = ({ min, max }) => range(min, max + 1).map(val => ({
    label: String(val),
    value: String(val),
}));

export const getCountAnswersShouldAnswered = question => {
    if (question?.type === QUESTION_TYPES.GAPS_IN_TEXT) return question.answers?.length || null;

    if (question?.type === QUESTION_TYPES.DISTRIBUTE_BY_CATEGORY) {
        return question.answers?.map(answer => answer.children || []).flat().length || null;
    }

    if (question?.type === QUESTION_TYPES.MATCHING) {
        const count = question.answers?.reduce((prev, curr) => {
            try {
                const answerData = JSON.parse(curr.data);

                if ('side' in answerData && answerData.side === 'right' && Array.isArray(answerData.correctAnswerId)) {
                    return prev + answerData.correctAnswerId.length;
                }
            } catch (e) {
                console.error('[getCountAnswersShouldAnswered]:', e);
            }

            return prev;
        }, 0);

        return count > 0 ? count : null;
    }

    return question?.answers.filter(answer => answer.is_correct).length || null;
};

/**
 * @param {object} newQuestion
 * @param {object} [prevQuestion]
 * @returns {*|(*&{question_data: string})}
 */
export const getNormalizeQuestionData = (newQuestion, prevQuestion) => {
    const shouldNormalize = VISIBILITY_MIN_ANSWERS.includes(newQuestion.type);

    if (!shouldNormalize) return newQuestion;

    const newQuestionData = JSON.parse(newQuestion.question_data);

    const MAX_ANSWER_LEVEL = getCountAnswersShouldAnswered(newQuestion);

    if (prevQuestion && prevQuestion.type === newQuestion.type) {
        const prevQuestionData = JSON.parse(prevQuestion.question_data);
        const prevCountAnswersShouldAnswered = getCountAnswersShouldAnswered(prevQuestion);

        if (prevQuestionData.minAnswersLevel === prevCountAnswersShouldAnswered) {
            newQuestionData.minAnswersLevel = MAX_ANSWER_LEVEL;
        }
    }

    const minAnswersLevel = newQuestionData.minAnswersLevel
        ? Math.max(MIN_ANSWER_LEVEL, Math.min(newQuestionData.minAnswersLevel, MAX_ANSWER_LEVEL))
        : MAX_ANSWER_LEVEL;

    return {
        ...newQuestion,
        question_data: JSON.stringify({
            ...newQuestionData,
            minAnswersLevel,
        }),
    };
};

export const getSimpleAnswer = (order, is_correct = 0, data = null) => ({
    title: '',
    is_correct,
    order,
    data,
});

export const getQuestion = ({
    quizType,
    questionType,
    title = '',
    description = '',
    sectionId = null,
}) => ({
    title,
    description,
    type: questionType,
    question_data: JSON.stringify(getDefaultQuestionOptions({
        quizType,
        questionType,
    })),
    answers: [
        getSimpleAnswer(1, 1),
        getSimpleAnswer(2, 0),
    ],
    is_archived: false,
    affects_result: false, // для проверки т.к. зависит от разных условий
    should_affect_result: true, // только для переключателя
    test_section_id: sectionId,
});

export const getSection = ({ title = '', order = 0 } = {}) => ({
    title,
    is_archived: false,
    is_invalid: true,
    should_passage_linear: false,
    should_shuffle_questions: true,
    display_questions_count: null,
    order,
    icon: null,
});

export const getNewQuestionRouteConditions = ({ order = 0, operator = null } = {}) => ({
    id: -1,
    answer_id: null,
    order,
    operator,
});

export const isShouldAnswerQuestion = q => !q.is_archived && q.type !== QUESTION_TYPES.INFORMATION;

export const isScoredQuestion = q => isShouldAnswerQuestion(q) && q.affects_result !== false;

export const getTotalQuestionsScore = ({ questions, scoreType, eachQuestionCount }) => sumBy(questions, question => {
    if (!isScoredQuestion(question)) return 0;

    if (scoreType === SCORE_TYPE.SAME_FOR_EACH_QUESTION) {
        return eachQuestionCount ?? 0;
    }

    const questionData = JSON.parse(question.question_data);

    return questionData.scoreCount ?? 1;
});
